import clsx from "clsx";
import React, { memo, useEffect, useRef, useState } from "react";
import { COLORS } from "../../../core/consts/redesign/commonRedesign";
import { Close } from "../../../core/consts/redesign/icons";
import useOutsideClickDetector from "../../../core/hooks/useOutsideClickDetector";
import IconButtonMain from "../../../UIcomponentsRedesign/IconButtonMain/IconButtonMain";
import classes from "./FilterProducts.module.scss";

/* Redesign . text input with filter */
type PropsType = {
  onChange: (val: string) => void;
  label?: string;
  options: ({ inn: string, companyName: string } | string)[];
  regularOnChange?: RegExp;
  placeholder?: string;
  style?: React.CSSProperties;
  id?: string;
  onClear?: () => void;
  initialFilter?: string;
}

const InputFilter = ({
  label,
  placeholder,
  id,
  options,
  onChange,
  onClear,
  style,
  initialFilter = ''
}: PropsType) => {
  const [filter, setFilter] = useState(initialFilter);
  const [focus, setFocus] = useState<boolean>(false);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [filteredOptions, setFilteredOptions] = useState<({ inn: string, companyName: string } | string)[]>(options);
  const inputFilterRef = useRef<HTMLDivElement>(null);
  useOutsideClickDetector(inputFilterRef, () => setShowOptions(false));

  useEffect(() => {
    if (initialFilter && initialFilter.length > 0) {
      setFilter(initialFilter)
    }
  }, [initialFilter]);

  useEffect(() => {
    setFilteredOptions(options || []);
  }, [options]);

  useEffect(() => {
    if (filter.length > 0) {
      const temp = options.filter(option => {
        if (typeof option === 'string') {
          return option.toLowerCase().includes(filter.toLowerCase());
        } else {
          return `${option.inn} ${option.companyName}`.toLowerCase().includes(filter.toLowerCase());
        }
      });
      setFilteredOptions(temp);
    } else {
      setFilteredOptions(options);
    }
  }, [filter, options]);

  const handleChangeFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputText = e.target.value;
    setShowOptions(true);
    setFilter(inputText);
  };

  const handleOptionClick = (option: { inn: string, companyName: string } | string) => {
    if (typeof option === 'string') {
      setFilter(option);
      onChange(option);
    } else {
      setFilter(option.inn);
      onChange(option.inn);
    }
    setShowOptions(false);
  };

  const handleCleanInput = () => {
    setShowOptions(false);
    setFilter('');
    onClear && onClear();
  }

  const renderHighlightedText = (option: string) => {
    if (!filter) return option;
    const parts = option.split(new RegExp(`(${filter})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === filter.toLowerCase() ? (
        <span key={index} className={classes.highlight}>{part}</span>
      ) : part
    );
  };

  const renderOptionText = (option: { inn: string, companyName: string } | string) => {
    if (typeof option === 'string') {
      return renderHighlightedText(option);
    } else {
      return renderHighlightedText(`${option.inn} - ${option.companyName}`);
    }
  };

  return (
    <div className={clsx(classes.inputBox,
      { [classes.inputBoxFocused]: focus })}
      style={style}
      id={id}
      ref={inputFilterRef}>
      <label className={classes.label}>{label}</label>
      <div className={classes.rowFlex}>
        <input
          type="text"
          value={filter}
          onChange={handleChangeFilter}
          placeholder={placeholder}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          className={classes.input}
        />
        {filter.length > 0 &&
          <IconButtonMain
            className={classes.closeIconInn}
            color="noBgGray"
            icon={<Close color={COLORS.gray2} />}
            handleClick={() => {
              handleCleanInput();
              setShowOptions(false)
            }}
          />}
      </div>
      {showOptions &&
        <div className={classes.smallDropdownList}>
          {filteredOptions?.map((option, index) => (
            <div
              key={index}
              className={classes.smallDropdownItem}
              style={{ marginBottom: 0 }}
              onClick={() => handleOptionClick(option)}
            >
              <div className={classes.optionItem}>
                {renderOptionText(option)}
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  )
}

export default memo(InputFilter)
